export const FaxBlockDocumentationMarkdown = `
### What does this block do?
The Fax Block sends a document via fax to a specified recipient via Documo service. The block requires a valid Document ID (typically generated from the Generate Document block) and verifies that the fax number is in a proper US format.

It supports options for attaching a Documo cover page and sending the fax in test mode using a sandbox environment. This block is designed to facilitate sending official documents like claims, referrals, or notices via fax.

### When to use it?
- Use this block when you need to send a document to a recipient's fax machine, either as part of routine communications (e.g., referrals, confirmations) or regulatory/legal submissions.
- Employ the block when documents must be sent with an official cover, or when verifying fax formatting and connectivity in test environments.
- Integrate this block into workflows that involve secure document transmission, such as insurance claim submissions or patient referral communications.

### Example flows
1. **Notice of Admission Submission to Insurance Payers:**

    After generating a notice of admission (NOA) document, the flow sends the document via fax to the insurer. The fax includes a cover page that summarizes the NOA details along with patient and provider information, ensuring that all relevant data is officially documented.

2. **Patient Referral Coordination:**

    In a referral workflow, once the referral document is generated, the Fax Block automatically sends the document to the receiving specialist's office. An optional Documo cover page can be attached, providing clear context such as the patient history, referral reason, and appointment details.

3. **Legal/Administrative Document Transmission:**

    For compliance and legal purposes, sensitive documents (e.g., consent forms or regulatory notices) are transmitted via fax. Using the Fax Block, the flow sends these documents with a dedicated cover page to guarantee that the communication is formal and verifiable, meeting organizational or legal requirements.

### How to configure this block
1. **File Reference (Document ID):**
    - Enter the Document ID into the "File Reference" field using the format {type}:{identifier}.
    - Ensure that this Document ID corresponds to a document created with the Generate Document block and has a valid Notable Document ID.

2. **Fax Number:**
    - Provide the recipient's fax number in the "Fax Number" field.
    - The field requires inclusion of the country code ("+1" or "1") and validates the input against formats such as:
      - 1xxxxxxxxxx
      - +1xxxxxxxxxx
      - 1xxx-xxx-xxxx
      - +1-xxx-xxx-xxxx
    - An error message ("Invalid US phone number") will display if the number is not in a valid format.

3. **Attach Documo Cover Page:**
    - Toggle this switch On if you wish to attach a Documo cover page.
    - When enabled, you must provide the Documo Cover Page ID (found in the documo cover sheet url) along with any optional recipient name, sender name, subject, and notes as needed by your cover page template.
    - The cover page id has to be valid for the environment you are using it in (i.e. if isTest is enabled, the cover sheet id has to come from sandbox). Otherwise, Documo won’t successfully send the fax

4. **Send as a Test Fax:**
    - Toggle this switch On to send the fax using the Documo sandbox environment (commonly used test fax number: +1-833-337-2477).
    - Leave it Off to send a fax to the actual recipient.

5. **Mock Output for Test Run:**
    - At the bottom, toggle the "Mock output for test run" switch to simulate fax sending.
    - When enabled, the block will not perform real fax transmissions but rather return preset data for testing flows.

### How to test?
1. **Mock Output Testing:**
    - Start by toggling "Mock output for test run" to On to simulate sending a fax and verify that the output returns expected success data without incurring costs.

2. **Shadow Testing:**
    - Verify you have a valid Document ID generated by the Generate Document block.
    - Toggle the Send as Test Fax switch On to send the fax using the Documo sandbox environment (commonly used test fax number: +1-833-337-2477).
    - View the sent document in Documo sandbox environment if needed.

3. **Live Run:**
    - Turn off test mode to perform a live send.
    - Monitor the returned information for success status and send timestamp to ensure the fax was processed correctly.

4. **Troubleshooting:**
    - If the fax fails, check that the Document ID is valid, the fax number is correctly formatted, and that your network connection is stable.
    - For test faxes, use the provided test fax number (+1-833-337-2477) if necessary.

### Best practices
  - **Validate Input Formats:**
    - Always ensure the fax number includes the correct country code and matches one of the valid US formats.
  - **Generate Valid Documents:**
    - Use the Generate Document block to create documents with a valid Notable Document ID prior to faxing.
  - **Test Before Sending:**
    - Utilize "Send as a Test Fax" and "Mock output for test run" options to confirm configuration and output before live transmission.
  - **Cover Page Configuration:**
    - When attaching a cover page, double-check the Documo Cover Page ID and related settings in the Documo portal.
  - **Monitor Output:**
    - Verify that the fax returns proper status and timestamp information to confirm that the document was sent successfully.
  - **File Size Limit:**
    - Be aware that the total file size should not exceed the 100MB limit.
`;
